/* eslint-disable consistent-return */
/* eslint-disable camelcase */
/**
 * @file loginView.jsx
 * @author Fernando Ruano
 * @description StepTwo View Page
 */

// Dependencies
import React from 'react';
import PropTypes from 'prop-types';
import { Button, Form, Input, Alert, Modal, Select } from 'antd';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import styled from 'styled-components';

// Components
import LoginTemplate from '@nephro/components/LoginTemplate';

// Helpers
import { getStorageItem } from '@nephro/commons/helpers/storage-helper';
import { onKeepOnlyNumbers } from '@nephro/commons/helpers/string-helper';

// Styles
import styles from './styles.module.css';

const StyledMainDiv = styled.div`
  .ant-select-single:not(.ant-select-customize-input) .ant-select-selector {
    height: 40px;
  }
  .ant-select-single:not(.ant-select-customize-input)
    .ant-select-selector
    .ant-select-selection-search-input {
    height: 38px;
  }

  .ant-select-single.ant-select-show-arrow .ant-select-selection-item,
  .ant-select-single.ant-select-show-arrow .ant-select-selection-placeholder {
    margin-top: 3px;
  }
`;

const { Option } = Select;
const { confirm: miniModal } = Modal;

function stepOneView({ patientActions, userActions, actions, loading }) {
  const { t: translate } = useTranslation();
  const [isError, setIsError] = React.useState(false);
  const [messageError, setMessageError] = React.useState(null);
  const [identificationTypesList, setIdentificationTypesList] =
    React.useState(null);
  const history = useHistory();
  const [form] = Form.useForm();

  const { getEmailPatientByDocument, getIdentificationTypesArray } =
    patientActions;
  const { getUserDataRegister } = userActions;
  const { setLoading } = actions;

  const validateMessages = {
    required: translate('commons.required')
  };

  // Function to get and set identification types list
  const onGetIdentificationTypesList = (_, response) => {
    if (response?.data?.content) {
      setIdentificationTypesList(response.data);
    }
  };

  React.useEffect(() => {
    if (getStorageItem('user')) {
      history.replace('/login');
    } else {
      getIdentificationTypesArray(
        { paged: false },
        onGetIdentificationTypesList
      );
    }
  }, []);

  // Modal confirmation when is an active user
  const showConfirm = () => {
    miniModal({
      title: translate('registration.activeUser'),
      icon: <ExclamationCircleOutlined />,
      content: '',
      okText: translate('registration.startSesion'),
      okCancel: false,
      onOk() {
        history.push('/login');
      }
    });
    setLoading(false);
  };

  // Navigate to defined url
  const navigateTo = (route, param = null) => history.push(route, param);

  const onNavigationBack = () => {
    navigateTo('/login');
  };

  // Get user info by ID
  const onGetUserLoginInfo = ({ type, response }, url) => {
    if (type === 'success') {
      const { document, docType } = form.getFieldsValue();
      const userInfoPayload = {
        document,
        docType,
        ...response.data
      };
      navigateTo(url, userInfoPayload);
    }
  };

  // Get patient info when user exists
  const onGetPatientInfo = (url, document, documentType) => {
    getUserDataRegister(
      { username: document, isPatient: true, documentType },
      (type, response) => onGetUserLoginInfo({ type, response }, url)
    );
  };

  // Validate if document exists
  const onValidate = (type, response) => {
    if (type === 'success') {
      showConfirm();
    } else {
      const { document, docType } = form.getFieldsValue();
      const { code } = response;
      switch (code) {
        case '400LGP02':
          return onGetPatientInfo('/registration/stepTwo', document, docType);
        case '400LGP03':
          return onGetPatientInfo('/registration/stepThree', document, docType);
        default:
          setLoading(false);
          setMessageError(
            response.code !== undefined
              ? `errors.${response.code}`
              : 'commons.problem'
          );
          setIsError(true);
          break;
      }
    }
  };

  // After press continue button when form full filled
  const onFinish = (values) => {
    setIsError(false);
    const { document } = values;
    const data = {
      document,
      documentType: values.docType
    };
    getEmailPatientByDocument(data, onValidate);
  };

  // After press continue button whit errors in form
  const onFinishFailed = () => setIsError(false);

  return (
    <StyledMainDiv>
      <LoginTemplate
        loading={loading}
        title="registration.hello"
        textContent="registration.welcome">
        <Form
          form={form}
          layout="vertical"
          requiredMark={false}
          onFinish={onFinish}
          onFinishFailed={onFinishFailed}
          onFieldsChange={onFinishFailed}
          validateMessages={validateMessages}>
          <Form.Item
            label={translate('patientMg.docType')}
            rules={[{ required: true, message: 'El tipo es requerido' }]}
            className={styles.mBottom}
            name={['docType']}>
            <Select
              placeholder={translate('patientMg.type')}
              optionFilterProp="children"
              filterOption={(input, option) =>
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }>
              {identificationTypesList?.content?.map((item, index) => (
                <Option key={index} value={item.identification_type_id}>
                  {item.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            label={translate('login.docid')}
            name="document"
            className="w-full"
            normalize={onKeepOnlyNumbers}
            rules={[
              {
                required: true
              }
            ]}>
            <Input className="h-10" placeholder="10567233" />
          </Form.Item>

          {isError && (
            <div className="mb-4 -mt-4">
              <Alert message={translate(messageError)} type="error" showIcon />
            </div>
          )}
          <div className="flex flex-row justify-between">
            <Form.Item className="w-full">
              <Button
                onClick={onNavigationBack}
                type="link"
                className={styles.stepOneButton}>
                {translate('commons.cancel')}
              </Button>
            </Form.Item>
            <Form.Item className="w-full">
              <Button
                className={styles.stepOneButton}
                type="primary"
                htmlType="submit">
                {translate('registration.continue')}
              </Button>
            </Form.Item>
          </div>
        </Form>
      </LoginTemplate>
    </StyledMainDiv>
  );
}

/**
 * Component definition of proptypes.
 */
stepOneView.propTypes = {
  actions: PropTypes.shape({
    setLoading: PropTypes.func.isRequired
  }).isRequired,
  patientActions: PropTypes.shape({
    getEmailPatientByDocument: PropTypes.func.isRequired,
    getIdentificationTypesArray: PropTypes.func.isRequired
  }).isRequired,
  userActions: PropTypes.shape({
    getUserLoginInfo: PropTypes.func.isRequired
  }).isRequired,
  loading: PropTypes.bool.isRequired
};

export default stepOneView;
